import { useOptionalDependency } from 'ditox-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FIREBASE_MANAGER_TOKEN } from '@/container';
import useUpdateDeviceToken from '@/hooks/fcm/use-update-device-token';
import { pushNotificationEventSchema } from '@/models/events/push-notification-event';
import { createNotificationStorage } from '@/services/storage-manager';
import { useUserId } from '@/stores/auth';
import { setNotificationPermissionRequested, useDeviceStore } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import { timeoutPromise } from '@/utils/promise';

export default function useNotificationPermission() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isSignedIn = useUserId();
  const firebaseManager = useOptionalDependency(FIREBASE_MANAGER_TOKEN);
  const { mutate: updateDeviceToken } = useUpdateDeviceToken();
  const { fcmToken, isNotificationPermissionRequested } = useDeviceStore();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (BUILD_EMBED_TYPE === 'embed-native') return;

    const channel = new BroadcastChannel('chat-notifications');
    const handler = async (evt?: MessageEvent) => {
      const store = createNotificationStorage();
      const value = evt?.data || (await store.getItem('pending-route'));
      const { success, data } = await pushNotificationEventSchema.safeParseAsync(value);

      if (success && data) {
        await store.removeItem('pending-route');
        navigate(data.route, { replace: location.pathname === data.route });
      }
    };

    channel.addEventListener('message', handler);
    navigator.serviceWorker.addEventListener('message', handler);
    if ('requestIdleCallback' in window) {
      const callbackId = requestIdleCallback(() => void handler());
      return () => {
        cancelIdleCallback(callbackId);
        navigator.serviceWorker.removeEventListener('message', handler);
        channel.removeEventListener('message', handler);
        channel.close();
      };
    }

    const timeoutId = setTimeout(() => void handler(), 0);
    return () => {
      clearTimeout(timeoutId);
      navigator.serviceWorker.removeEventListener('message', handler);
      channel.removeEventListener('message', handler);
      channel.close();
    };
  }, [navigate]);

  useEffect(() => {
    if (firebaseManager) {
      (async () => {
        const shouldPrompt = await firebaseManager.shouldRequestPermission();

        if (shouldPrompt && !isNotificationPermissionRequested) {
          const resolution = await makePrompt({
            key: 'request-notification-permission',
            type: 'dialog',
            title: t('permission-request.notifications.title'),
            content: t('permission-request.notifications.content'),
          });

          setNotificationPermissionRequested(true);
          if (resolution === true || resolution === 'confirm') {
            await timeoutPromise(
              async () => firebaseManager.initialize(),
              3_000,
              () => void 0,
            );
          }
        }
      })();
    }
  }, [firebaseManager, isNotificationPermissionRequested]);

  useEffect(() => {
    if (firebaseManager) {
      (async () => {
        const canSendNotification = await firebaseManager.shouldRequestPermission('granted');
        if (isSignedIn && canSendNotification) {
          setNotificationPermissionRequested(true);
          await firebaseManager.initialize().catch(console.warn);
        }
      })();
    }
  }, [isSignedIn, firebaseManager]);

  useEffect(() => {
    if (fcmToken && isSignedIn) {
      updateDeviceToken(fcmToken);
    }
  }, [fcmToken, isSignedIn]);
}
